import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from 'react'

import Skeleton from '@mui/material/Skeleton'
import clsx from 'clsx'
import { ConfirmationDialogRunning } from 'components/atoms/ConfirmationDialogRunning'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { campaignApi } from 'features/campaigns'
import { useChatContact } from 'features/contacts/hooks'
import { AddOneContact } from 'features/contacts/ui'
import { useMostRelevant } from 'features/executions/hooks'
import { notificationUtils } from 'features/notifications'
import { useSwitcher } from 'shared/hooks'
import { typo } from 'shared/styles'
import { StackIcon } from 'shared/ui'

import type { Execution } from '../../types'

import { api } from '../../api'
import css from './ChatHeader.module.css'
import { Name } from './Name'

const enum StatusText {
	Cancel = 'Sequence has been cancelled',
	Completed = 'Sequence has been completed',
	None = 'Contact does not participate in any campaign',
	Running = 'Campaign is running',
	Stopped = 'Campaign is paused',
	Success = 'Got a reply, the sequence is paused'
}

const TEXT_CN = clsx(css.text, typo.caption, css.withDot)

const CancelButton = (execution: ConfirmActionExecution) => (
	<ActionWithConfirm
		action='complete'
		confirmKey='1.1'
		label={[
			<StackIcon icon='complete' key='i' stack='campaign' width={14} />,
			'Cancel sequence'
		]}
		mutation='completeExecution'
		snack='n3.34'
		{...execution}
	/>
)
const ResumeButton = (execution: ConfirmActionExecution) => (
	<ActionWithConfirm
		action='resume'
		confirmKey='c1.16'
		label={[
			<StackIcon icon='run' key='i' stack='campaign' width={14} />,
			'Resume sequence'
		]}
		mutation='resume'
		snack='n3.33'
		{...execution}
	/>
)

const NoExecution = () => [
	<p className={clsx(typo.caption, css.text)} key='💬'>
		<StackIcon className={css.info} icon='info' width={14} />
		{StatusText.None}
	</p>,
	<AddCampaignWithCheck key='➕' />
]

const StatusSkeleton = (
	<span className={clsx(css.text, typo.caption)}>
		<Skeleton
			className={css.skeletonCircle}
			height='0.625rem'
			variant='circular'
			width='0.625rem'
		/>
		<Skeleton variant='text' width={'30ch'} />
	</span>
)

export function ChatHeader({
	children,
	className
}: ComponentPropsWithoutRef<'div'>) {
	const { currentData: execution, isSuccess } = useMostRelevant(true)
	const isNoExecution = isSuccess && typeof execution === 'string'
	const isExistExecution = isSuccess && typeof execution === 'object'

	const campaignId = isExistExecution ? execution.campaign_id : ''

	return (
		<header className={clsx(css.root, className)}>
			<Name id={campaignId} noData={isNoExecution} />
			{!(isNoExecution || isExistExecution) && StatusSkeleton}
			{isNoExecution && <NoExecution />}
			{isExistExecution && <ExecutionStatus {...execution} />}

			{children}
		</header>
	)
}
function ActionWithConfirm({
	action,
	actions,
	confirmKey,
	id,
	label,
	mutation,
	snack
}: ConfirmActionExecution & {
	action: 'complete' | 'resume'
	confirmKey: '1.1' | 'c1.16'
	label: ReactNode
	mutation: 'completeExecution' | 'resume'
	snack: 'n3.33' | 'n3.34'
}) {
	const switcher = useSwitcher()
	const isEnabled = actions[action].enabled
	const [send, mutationOptions] = api.endpoints[mutation].useMutation()

	if (!isEnabled) {
		return null
	}

	return (
		<>
			<button
				className={clsx(css.action, typo.buttonSm)}
				onClick={switcher.on}
				type='button'
			>
				{label}
			</button>

			<ModalWindow
				closeHandler={switcher.off}
				closeText='No'
				confirmHandler={async () => {
					if (mutationOptions.isLoading || !id) {
						return
					}

					const { data } = await send(id)

					if (data !== undefined) {
						notificationUtils.snackById(snack)
					}
				}}
				confirmText='Yes'
				loading={mutationOptions.isLoading}
				{...CONSTS.confirmationDialogs[confirmKey]}
				open={switcher.isOn}
			/>
		</>
	)
}

function AddCampaignWithCheck() {
	const { currentData: contact } = useChatContact()

	return contact && <AddToCampaign entityId={contact ? contact.id : ''} />
}

function AddToCampaign({ entityId }: { entityId?: string }) {
	const switcher = useSwitcher()

	return (
		<>
			<button
				className={clsx(css.action, typo.buttonSm)}
				onClick={switcher.on}
				type='button'
			>
				<StackIcon icon='plus' width={14} />
				Add to campaign
			</button>

			<AddOneContact
				entityId={entityId}
				isOpen={switcher.isOn}
				onRequestClose={switcher.off}
			/>

			<ConfirmationDialogRunning />
		</>
	)
}

const CompletedStatus = ({ id }: { id: string }) => [
	<p
		className={TEXT_CN}
		key='💬'
		style={
			{
				'--dot-c': 'var(--status-completed)'
			} as CSSProperties
		}
	>
		{StatusText.Completed}
	</p>,
	<AddToCampaign entityId={id} key='➕' />
]

type ConfirmActionExecution = Pick<Execution, 'actions' | 'id'>

function ExecutionStatus({
	actions,
	campaign_id: campaignId,
	id,
	linkedin_contact: linkedinContact,
	state,
	step_executions: steps
}: Execution) {
	switch (state) {
		case 'Cancelled':
			return [
				<p
					className={TEXT_CN}
					key='💬'
					style={
						{
							'--dot-c': 'hsl(274deg 100% 85%)'
						} as CSSProperties
					}
				>
					{StatusText.Cancel}
				</p>,
				<AddToCampaign entityId={linkedinContact.id} key='➕' />
			]

		case 'Failed':
		case 'Ignored':
		case 'Skipped':
			return <CompletedStatus id={linkedinContact.id} />

		case 'InProgress':
		case 'NotStarted':
			return [
				<InProcessStatus campaignId={campaignId} key='⏳' />,
				<CancelButton actions={actions} id={id} key='⏹️' />
			]
		case 'Succeed': {
			const isCompleted = steps.every(step => step.is_settled)

			return isCompleted ? (
				<CompletedStatus id={linkedinContact.id} />
			) : (
				[
					<p
						className={TEXT_CN}
						key='💬'
						style={
							{
								'--dot-c': 'var(--status-paused)'
							} as CSSProperties
						}
					>
						{StatusText.Success}
					</p>,
					<ResumeButton actions={actions} id={id} key='▶️' />
				]
			)
		}
	}
}

function InProcessStatus({ campaignId }: { campaignId: string }) {
	const campaignQuery = campaignApi.useGetCampaignsByIdQuery(campaignId, {
		skip: !campaignId
	})

	const campaign =
		campaignId &&
		campaignQuery.isSuccess &&
		campaignQuery.data.id === campaignId
			? campaignQuery.data
			: null

	if (!campaign) {
		return StatusSkeleton
	}

	let text = ''
	const style: Record<string, string> = {}

	if (campaign.state === 'Running') {
		text = StatusText.Running
		style['--dot-c'] = 'var(--status-running)'
	}

	if (campaign.state === 'Stopped') {
		text = StatusText.Stopped
		style['--dot-c'] = 'var(--status-paused)'
	}

	return (
		<p className={TEXT_CN} style={style as CSSProperties}>
			{text}
		</p>
	)
}
